import React, { useState, useEffect, useRef } from 'react';
import MathJax from "../../../components/react-mathjax-preview";
import MathParserUtil from '../../../lib/math-parser';
import "./singleQuestionAnswerView.css";
import { Grid, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import SFMCRadioTypeAnswers from './components/answers/SFMCRadioTypeAnswers';
import { NotificationManager } from 'react-notifications';
import ApiRequestService from '../../../components/ApiRequestService';
import SFMCChecboxTypeAnswers from './components/answers/SFMCChecboxTypeAnswers';
import SFTextboxTypeAnswer from './components/answers/SFTextboxTypeAnswer';
import { Divider } from 'react-md';
import MQTQuestion from '../../../ui/takeQuiz/mqt/MQTQuestion';
import { PiCaretDownBold } from "react-icons/pi";
import SFAssessmentObjectivesGrid from './components/answers/SFAssessmentObjectivesGrid';
import SFExcludeAOCheckboxAnswers from './components/answers/SFExcludeAOCheckboxAnswers';
import { AutomationType, CheckDateValidation, CheckEmailValidation, CheckNumberValidation } from '../../../common/constants';

const getCleanQuestionText = (questionText) => String(questionText || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const SFSingleQuestionAnswerView = ({
    userID,
    quizID,
    courseID,
    sectionID,
    currentQuestion,
    overAllQuestionCount,
    questionFocusId,

    isOnePageTest=false,

    // functions
    onSaveSubQuestionsComplete,
}) => {

    const [hasSubQuestions, setHasSubQuestions] = useState(false);
    const [startTime, setStartTime] = useState(new Date());
    const tutorialQuestionText = questionFocusId
        ? [currentQuestion?.question_text, ...(currentQuestion?.sub_questions || []).map(question => question.question_text)]
            .map(getCleanQuestionText)
            .filter(Boolean)
            .join(' ')
        : '';

    useEffect(() => {
        // console.debug("currentQuestion>>??", currentQuestion)
        if (currentQuestion && currentQuestion.sub_questions && currentQuestion.sub_questions.length) {
            setHasSubQuestions(true);
            setStartTime(new Date());
        }
    }, [currentQuestion]);


    return (
        <div
            className='bg-class'
            id={questionFocusId}
            tabIndex={questionFocusId ? 0 : undefined}
            role={questionFocusId ? 'region' : undefined}
            aria-label={questionFocusId ? `Question. ${tutorialQuestionText}` : undefined}
        >
            <div className='mb-2'>
                <div className='parent-question p-3 '>
                    {/* <div>
                        <Typography fontWeight={600}>Question:</Typography>
                    </div> */}
                    <div>
                        <DisplayQuestion question={currentQuestion} />
                    </div>
                </div>
                <Divider />
            </div>

            {hasSubQuestions ? <SubQuestions
                isOnePageTest={isOnePageTest}
                startTime={startTime}
                userID={userID}
                quizID={quizID}
                sectionID={sectionID}
                courseID={courseID}
                parentQuestion={currentQuestion}
                overAllQuestionCount={overAllQuestionCount}

                // functions
                onSaveSubQuestionsComplete={onSaveSubQuestionsComplete}
            /> : null}
        </div>
    );
}

const SubQuestions = ({ startTime, userID, parentQuestion, courseID, quizID, sectionID, onSaveSubQuestionsComplete, overAllQuestionCount, isOnePageTest }) => {
    const [subQuestions, setSubQuestions] = useState([]);
    const [answersMap, setAnswersMap] = useState({});
    const answersMapRef = useRef({});
    const [allExpanded, setAllExpanded] = useState(
        parentQuestion?.sub_question_accordion_default_expanded !== 0
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (parentQuestion && parentQuestion.sub_questions) {
            setSubQuestions(parentQuestion.sub_questions)
            setAnswersMap({})
            answersMapRef.current = {};
        }
        setAllExpanded(parentQuestion?.sub_question_accordion_default_expanded !== 0);
    }, [parentQuestion])

    const removeElementFromArray = (arr, element) => {
        var i = arr.indexOf(element);
        if (i !== -1) {
            arr.splice(i, 1);
        }
        return arr;
    }

    const toRoman = (num) => {
        const romanMap = [
            { value: 1000, symbol: "M" },
            { value: 900, symbol: "CM" },
            { value: 500, symbol: "D" },
            { value: 400, symbol: "CD" },
            { value: 100, symbol: "C" },
            { value: 90, symbol: "XC" },
            { value: 50, symbol: "L" },
            { value: 40, symbol: "XL" },
            { value: 10, symbol: "X" },
            { value: 9, symbol: "IX" },
            { value: 5, symbol: "V" },
            { value: 4, symbol: "IV" },
            { value: 1, symbol: "I" },
        ];

        let result = "";
        for (let i = 0; i < romanMap.length; i++) {
            while (num >= romanMap[i].value) {
                result += romanMap[i].symbol;
                num -= romanMap[i].value;
            }
        }
        return result;
    };

    const onAnswerChoice = (subQindex, answerValue, answerIndex, inputBoxValue) => {
        let subQuestion = subQuestions[subQindex];
        console.debug("value>>??", subQuestion, subQindex, answerValue, answerIndex);

        setAnswersMap(prev => {
            let answerObj = {
                course_id: courseID,
                section_id: sectionID,
                quiz_id: quizID,
                subject_id: subQuestion.subject_id,
                topic_id: subQuestion.topic_id,
                category_id: subQuestion.category_id,
                subcategory_id: subQuestion.subcategory_id,
                question_id: subQuestion.question_id,
                question_type: subQuestion.question_type,
                answer_type: subQuestion.answer_type,
                over_all_question_count: overAllQuestionCount,
                user_id: userID,
                question_data: subQuestion,
                is_sub_question: true,
                answer_text: "",
            }

            if (subQuestion.question_type === 0) { // STANDARD
                switch (subQuestion.answer_type) {
                    case 0: // multiple choice radio
                        answerObj.selected_answer = `${answerValue}`;
                        if (inputBoxValue) {
                            answerObj.answer_text = `${inputBoxValue}`;
                        }
                        answerObj.chosen_answer_array = [parseInt(answerValue, 10)];
                        break;
                    case 1: // multiple choice checkbox
                        let currentAnswerObj = prev[subQindex];
                        let parsedAnswerValue = parseInt(answerValue, 10);
                        var tempSelectedAnswerArr = currentAnswerObj?.selected_answer
                            ? [...currentAnswerObj.selected_answer]
                            : undefined;
                        if (tempSelectedAnswerArr !== undefined &&
                            (tempSelectedAnswerArr.includes(parsedAnswerValue)) === true) {
                            tempSelectedAnswerArr = removeElementFromArray(tempSelectedAnswerArr, parsedAnswerValue);
                        }
                        else {
                            if (tempSelectedAnswerArr === undefined) {
                                tempSelectedAnswerArr = [];
                            }
                            tempSelectedAnswerArr.push(parsedAnswerValue);
                        }
                        console.debug("tempSelectedAnswerArr>>??", tempSelectedAnswerArr);
                        answerObj.selected_answer = tempSelectedAnswerArr;
                        answerObj.chosen_answer_array = tempSelectedAnswerArr;
                        break;
                    case 2: // text box
                        answerObj.selected_answer = answerValue;
                        answerObj.answer_text = answerValue
                        break;

                    default:
                        break;
                }
            } else if (subQuestion.question_type === 2) { // MQG
                let newSelectedAnswerMap = {};
                Object.keys(answerValue).map(key => {
                    newSelectedAnswerMap[key] = answerValue[key];
                })
                answerObj.selected_answer_map = newSelectedAnswerMap;
                let answerText = [];
                Object.keys(answerValue).map(key => {
                    answerText.push(answerValue[key])
                })
                answerObj.selected_answer = JSON.stringify(answerValue);
                answerObj.answer_text = JSON.stringify(answerText);
            } else if (subQuestion.question_type === 5) { // Assessment Objectives
                answerObj.selected_answer = JSON.stringify(answerValue);
                answerObj.answer_text = JSON.stringify(answerValue);
            }

            let newAnswersMap = { ...prev, [subQindex]: {...answerObj} };
            answersMapRef.current = newAnswersMap;
            console.debug("tempAnswersMap", newAnswersMap)
            return newAnswersMap;
        })
    }

    const handleSaveAllSubQuestionTest = () => {
        // check if all subquestion answers are filled
        const currentAnswersMap = answersMapRef.current;
        console.debug("save answersMap", currentAnswersMap);
        if (Object.keys(currentAnswersMap) && Object.keys(currentAnswersMap).length < subQuestions.length) {
            NotificationManager.warning("Please answer all subquestions before submitting.", '', 3000)
            return;
        }

        setIsSubmitting(true);

        // Check for answer validation if its an automation question
        let hasValidationError = false;
        for (const key of Object.keys(currentAnswersMap)) {
            let valuObj = currentAnswersMap[key];
            if (valuObj && valuObj.question_data && valuObj.question_data.is_automation) {

                let automationType = valuObj.question_data.automation_type;
                if (automationType === AutomationType.END_DATE) {
                    currentAnswersMap[key].answer_text = currentAnswersMap[key].answer_text.trim()
                    let val = currentAnswersMap[key].answer_text;
                    if (!CheckDateValidation(val)) {
                        NotificationManager.error('Please enter a valid date in MM/DD/YYYY format.', 'Invalid Date', 3000);
                        hasValidationError = true;
                        break;
                    }
                } else if (automationType === AutomationType.TEST_TIME) {
                    currentAnswersMap[key].answer_text = currentAnswersMap[key].answer_text.trim()
                    let val = currentAnswersMap[key].answer_text;
                    if (!CheckNumberValidation(val)) {
                        NotificationManager.error('Please enter a valid numeric value.', 'Invalid Input', 3000);
                        hasValidationError = true;
                        break;
                    }
                } else if (automationType === AutomationType.QUESTION_BANK) {
                    let answerText = currentAnswersMap[key].answer_text;
                    let answerJSON = JSON.parse(answerText)
                    if (answerJSON["covered"].length === 0 && answerJSON["want_to_assess"].length === 0) {
                        NotificationManager.error('Please choose atlease one assessment Objectives', 'Empty Answer', 3000);
                        hasValidationError = true;
                        break;
                    }
                } else if (automationType === AutomationType.EMAIL) {
                    currentAnswersMap[key].answer_text = currentAnswersMap[key].answer_text.trim()
                    let answerText = currentAnswersMap[key].answer_text;
                    if (!CheckEmailValidation(answerText)) {
                        NotificationManager.error('Please enter a valid email address.', 'Invalid Email', 3000);
                        hasValidationError = true;
                        break;
                    }
                } else if (automationType === AutomationType.EXCLUDE_AO_CONTEXT) {
                    try {
                        let answerText = currentAnswersMap[key].selected_answer || currentAnswersMap[key].answer_text;
                        let parsed = typeof answerText === 'string' ? JSON.parse(answerText) : answerText;
                        let excludedAo = parsed && parsed.excluded_ao ? parsed.excluded_ao : [];
                        if (!Array.isArray(excludedAo) || excludedAo.length === 0) {
                            NotificationManager.error('Please select at least one option.', 'Empty Answer', 3000);
                            hasValidationError = true;
                            break;
                        }
                    } catch (e) {
                        NotificationManager.error('Please select at least one option.', 'Empty Answer', 3000);
                        hasValidationError = true;
                        break;
                    }
                }
            }
        }
        if (hasValidationError) {
            setIsSubmitting(false);
            return;
        }

        let payloadArr = Object.values(currentAnswersMap)

        // Now append the parent question payload to payloadArr
        let parentAnswer = {
            course_id: courseID,
            section_id: sectionID,
            quiz_id: quizID,
            subject_id: parentQuestion.subject_id,
            topic_id: parentQuestion.topic_id,
            category_id: parentQuestion.category_id,
            subcategory_id: parentQuestion.subcategory_id,
            question_id: parentQuestion.question_id,
            question_type: parentQuestion.question_type,
            answer_type: parentQuestion.answer_type,
            over_all_question_count: overAllQuestionCount,
            user_id: userID,
            is_sub_question: false,
            is_parent_question: true,
            question_data: parentQuestion,
            que_elapsed_time: Math.trunc((new Date().getTime() - startTime) / 1000),
        }
        payloadArr.push(parentAnswer)
        console.debug("payloadArr>>??", payloadArr, currentAnswersMap)

        onSaveTest(payloadArr)
    }

    const onSaveTest = (payloadArr) => {
        ApiRequestService({
            method: 'post',
            url: '/saveTestSubQuestions',
            data: payloadArr,
        }).then(res => {
            let resultArr = res?.data?.data;
            console.debug("res>>??", res, resultArr);
            if (resultArr && resultArr.length) {
                let parentQuestionSaveRes = resultArr[resultArr.length - 1];
                console.debug("parentQuestionSaveRes", parentQuestionSaveRes)
                if (parentQuestionSaveRes) {
                    if (onSaveSubQuestionsComplete) {
                        onSaveSubQuestionsComplete(parentQuestionSaveRes);
                    }
                }
            }
        }).catch(error => {
            console.error("Error saving test:", error);
            NotificationManager.error("Failed to submit answers. Please try again.", '', 3000);
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <div className='px-3 pt-2'>
            {parentQuestion.sub_question_accordion_mode === 1 ? 
                <div className='expand-collapse-btn mb-2'>
                    <Button size='small' variant='outlined' onClick={() => setAllExpanded(true)}>
                        Expand All
                    </Button>
                    <Button size='small' variant='outlined' onClick={() => setAllExpanded(false)}>
                        Collapse All
                    </Button>
                </div>
            : null}
            <Grid
                container
                spacing={4}
                className="sub-grid"
                justifyContent={
                    parentQuestion.sub_question_display_mode === 0
                        ? "center"
                        : "flex-start"
                }
            >
                {subQuestions && subQuestions.length
                    ? subQuestions.map((subQuest, subQindex) => {
                        return (
                            <Grid
                                item
                                key={subQindex}
                                xs={12}
                                sm={
                                    parentQuestion.sub_question_display_mode === 0
                                        ? 12
                                        : 6
                                }
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        parentQuestion.sub_question_display_mode === 0
                                            ? "center"
                                            : "flex-start",
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        maxWidth:
                                            parentQuestion.sub_question_display_mode === 0
                                                ? "800px"
                                                : "100%",
                                    }}
                                    className="d-flex align-items-start"
                                >
                                    <div className="mr-2">
                                        {toRoman(subQindex + 1).toLocaleLowerCase()}.
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <QuestionAnswerContainer
                                            showAccordion={parentQuestion.sub_question_accordion_mode === 1}
                                            expanded={allExpanded}
                                            question={subQuest}
                                            onAnswerChoice={(answerValue, answerIndex) =>
                                                onAnswerChoice(
                                                    subQindex,
                                                    answerValue,
                                                    answerIndex
                                                )
                                            }
                                            onInputBoxChange={(answerValue, answerIndex, inputBoxValue) =>
                                                onAnswerChoice(
                                                    subQindex,
                                                    answerValue,
                                                    answerIndex,
                                                    inputBoxValue
                                                )
                                            }
                                            selectedAnswers={
                                                answersMap[subQindex]?.selected_answer
                                            }
                                        />
                                    </div>
                                </div>
                            </Grid>
                        );
                    })
                    : null}
            </Grid>

            {isOnePageTest === false ?
            <div className='subquestion-takequiz-footer'>
                <Button
                    raised
                    variant='contained'
                    onClick={handleSaveAllSubQuestionTest}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Submit Answer'}
                </Button>
            </div>
            : null}
        </div>
    )
}


const QuestionAnswerContainer = ({ showAccordion: showAccordionProp, expanded: expandedProp, question, onAnswerChoice, onInputBoxChange, selectedAnswers }) => {
    const [showAccordion, setShowAccordion] = useState(showAccordionProp);
    const [expanded, setExpanded] = useState(expandedProp);

    useEffect(() => {
        setExpanded(expandedProp);
    }, [expandedProp]);
    
    return (
        <div className='question-answer-container'>
            <div className='d-flex justity-space-betweeen align-items-center' style={{gap: "6px"}}>
                <div className='w-100'>
                    <DisplayQuestion question={question} />
                </div>
                {showAccordion ? 
                    <IconButton onClick={() => {
                        setExpanded(!expanded);
                    }}>
                        <PiCaretDownBold className={`accordion-caret ${expanded ? 'accordion-caret-expanded' : 'accordion-caret-collapsed'}`} />
                    </IconButton> 
                : null}
            </div>
            <div className={`accordion-content ${(!showAccordion || expanded) ? 'accordion-content-expanded' : 'accordion-content-collapsed'}`}>
                <div className='mt-3'>
                    <DisplayAnswer question={question} onAnswerChoice={onAnswerChoice} 
                        selectedAnswers={selectedAnswers} 
                        onInputBoxChange={onInputBoxChange}
                        />
                </div>
            </div>
        </div>
    )
}

const DisplayQuestion = ({ question }) => {
    const [questionText, setQuestionText] = useState("");
    const [vectorVariableMap, setVectorVariableMap] = useState(null);

    useEffect(() => {
        if (question) {
            setQuestionText(question.question_text)
        }
    }, [question])

    return (
        <React.Fragment>
            <MathJax
                math={MathParserUtil.setVariablesToText(questionText, vectorVariableMap, { formatValues: true })}
            />
        </React.Fragment>
    )
}

const DisplayAnswer = ({ question, onAnswerChoice, selectedAnswers, onInputBoxChange }) => {
    let answerDisplay;

    console.debug("question>>??", question)

    // EXCLUDE AO (Assessment Link + Exclude AO mapping)
    if (question.is_automation && question.automation_type === AutomationType.EXCLUDE_AO_CONTEXT) {
        const subcatKeys = question.excluded_ao_list
            ? (typeof question.excluded_ao_list === 'string' ? JSON.parse(question.excluded_ao_list) : question.excluded_ao_list)
            : [];
        return <SFExcludeAOCheckboxAnswers
            selectedSubcategoryKeys={subcatKeys}
            onAnswerEntered={(val) => onAnswerChoice(val)}
            selectedAnswers={selectedAnswers}
        />;
    }

    // STANDARD + RADIO
    if (question.question_type === 0 && question.answer_type === 0) {
        let displayAnswerArr = question.answers;

        displayAnswerArr?.forEach((_, index) => {
            if (displayAnswerArr[index].isChecked) {
                displayAnswerArr[index].isChecked = false
            }
        })

        answerDisplay = <SFMCRadioTypeAnswers
            displayAnswerArr={displayAnswerArr}
            onAnswerChoice={onAnswerChoice}
            selectedAnswers={selectedAnswers}
            numOfColumns={question.answerDisplayStyle}
            currentQuestionType={question.question_type}
            currentVariableMap={null}
            isMobile={false}
            showAnswerResponse={false}
            answerCorrect={false}
            showSubmitLoader={false}
            vectorSettingsData={null}
            currentAnswerType={question.answer_type}
            onInputBoxChange={(id, index, val) => {
                onInputBoxChange(id, index, val)
            }}
        />
        return answerDisplay
    }

    // STANDARD + CHECKBOX
    if (question.question_type === 0 && question.answer_type === 1) {
        let displayAnswerArr = question.answers;

        displayAnswerArr?.forEach((_, index) => {
            if (displayAnswerArr[index].isChecked) {
                displayAnswerArr[index].isChecked = false
            }
        })

        answerDisplay = <SFMCChecboxTypeAnswers
            displayAnswerArr={displayAnswerArr}
            selectedAnswerArray={selectedAnswers ? selectedAnswers.map(ans => `${ans}`) : []}
            onAnswerChoice={onAnswerChoice}
            numOfColumns={question.answerDisplayStyle}
            currentQuestionType={question.question_type}
            currentVariableMap={null}
            isMobile={false}
            showAnswerResponse={false}
            answerCorrect={false}
            showSubmitLoader={false}
            currentAnswerType={question.answer_type}
            compact={false}
        />
        return answerDisplay
    }

    // STANDARD + TEXTBOX
    if (question.question_type === 0 && question.answer_type === 2) {
        let displayAnswerArr = question.answers;

        displayAnswerArr?.forEach((_, index) => {
            if (displayAnswerArr[index].isChecked) {
                displayAnswerArr[index].isChecked = false
            }
        })

        answerDisplay = <SFTextboxTypeAnswer
            key={question.question_id}
            onAnswerChoice={onAnswerChoice}
            isMobile={false}
            compact={true}
            textValue={selectedAnswers}
        />
        return answerDisplay
    }

    // MULTIPLE GRID QUESTION + RADIO
    if (question.question_type === 2) {
        answerDisplay = <MQTQuestion
            showSelectAll={question.mqg_enable_column_select_all === 1}
            mqt_options={question.mqt_options}
            onAnswerChoice={(selectedAnswerMap) => { 
                onAnswerChoice(selectedAnswerMap)
            }}
            questionID={question.question_id}
            showMQTAnswers={false}
            isMobile={false}
            showAnswerResponse={false}
            answerCorrect={false}
            showSubmitLoader={false}
            count={1}
            compact={false}
            selectedAnswers={selectedAnswers}
            question_attempt={1}
            alreadyPickedAnswer={null}
        />
        return answerDisplay
    }

    // ASSESSMENT OBJECTIVES
    if (question.question_type === 5) {
        const subcatKeys = question.assessment_objectives_subcategory_keys
            ? question.assessment_objectives_subcategory_keys.split(',').filter(Boolean)
            : [];
        return <SFAssessmentObjectivesGrid
            selectedSubcategoryKeys={subcatKeys}
            onAnswerChoice={onAnswerChoice}
            selectedAnswers={selectedAnswers}
        />;
    }

}

export default SFSingleQuestionAnswerView;
